import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { toast } from 'react-toastify'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { protectedActivityService } from '../../../services/admin/protectedActivityService'
import { processTemplateService } from '../../../services/processTemplateService'
import { workflowService } from '../../../services/admin/workflow'
import { adminTeamsService } from '../../../services/admin/teams'
import ConfirmationBox from '../../../components/Dashboard/ConfirmationBox'

const ProtectedActivityConfig = () => {
    const { user } = useAuthContext()
    const [loading, setLoading] = useState(false)
    const [mappings, setMappings] = useState([])
    const [processTemplates, setProcessTemplates] = useState([])
    const [activityTemplates, setActivityTemplates] = useState([])
    const [teams, setTeams] = useState([])

    const [selectedProcessTemplateId, setSelectedProcessTemplateId] = useState('')
    const [selectedActivityTemplateId, setSelectedActivityTemplateId] = useState('')
    const [selectedTeamId, setSelectedTeamId] = useState('')

    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
    const [deleteTarget, setDeleteTarget] = useState(null)

    const fetchData = async () => {
        setLoading(true)
        try {
            const [mappingsData, ptData, teamsData] = await Promise.all([
                protectedActivityService.getProtectedActivities(user),
                processTemplateService.getAllProcessTemplates(user),
                adminTeamsService.getTeams(user),
            ])
            setMappings(mappingsData)
            setProcessTemplates(ptData)
            setTeams(teamsData)
        } catch (error) {
            console.error('Error fetching data:', error)
            toast.error('Failed to load data')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (user) {
            fetchData()
        }
    }, [user])

    useEffect(() => {
        const fetchActivities = async () => {
            if (selectedProcessTemplateId && user) {
                try {
                    const activities = await workflowService.getActivities(user, selectedProcessTemplateId)
                    setActivityTemplates(activities)
                } catch (error) {
                    console.error('Error fetching activities:', error)
                    toast.error('Failed to load activities')
                }
            } else {
                setActivityTemplates([])
            }
        }
        fetchActivities()
    }, [selectedProcessTemplateId, user])

    const handleAddMapping = async () => {
        if (!selectedTeamId || !selectedActivityTemplateId) {
            toast.error('Please select both an activity and a team.')
            return
        }

        try {
            await protectedActivityService.addProtectedActivity(user, {
                team_id: selectedTeamId,
                activity_template_id: selectedActivityTemplateId,
            })
            toast.success('Mapping added successfully')
            fetchData() // Refresh list
            setSelectedActivityTemplateId('')
            setSelectedTeamId('')
        } catch (error) {
            console.error('Error adding mapping:', error)
            toast.error(error.response?.data?.error || 'Failed to add mapping')
        }
    }

    const handleDeleteClick = (mapping) => {
        setDeleteTarget(mapping)
        setDeleteConfirmationOpen(true)
    }

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return

        try {
            await protectedActivityService.deleteProtectedActivity(
                user,
                deleteTarget.team_id,
                deleteTarget.activity_template_id
            )
            toast.success('Mapping deleted successfully')
            fetchData()
        } catch (error) {
            console.error('Error deleting mapping:', error)
            toast.error('Failed to delete mapping')
        } finally {
            setDeleteConfirmationOpen(false)
            setDeleteTarget(null)
        }
    }

    if (loading && mappings.length === 0) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                }}
            >
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box p={3}>
            <Typography variant="h5" gutterBottom>
                Protected Activity Configuration
            </Typography>

            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Process Template</InputLabel>
                            <Select
                                value={selectedProcessTemplateId}
                                label="Process Template"
                                onChange={(e) => {
                                    setSelectedProcessTemplateId(e.target.value)
                                    setSelectedActivityTemplateId('')
                                }}
                            >
                                {processTemplates.map((pt) => (
                                    <MenuItem key={pt.id} value={pt.id}>
                                        {pt.process_template_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small" disabled={!selectedProcessTemplateId}>
                            <InputLabel>Activity Template</InputLabel>
                            <Select
                                value={selectedActivityTemplateId}
                                label="Activity Template"
                                onChange={(e) => setSelectedActivityTemplateId(e.target.value)}
                            >
                                {activityTemplates.map((at) => (
                                    <MenuItem key={at.id} value={at.id}>
                                        {at.activity_template_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Allowed Team</InputLabel>
                            <Select
                                value={selectedTeamId}
                                label="Allowed Team"
                                onChange={(e) => setSelectedTeamId(e.target.value)}
                            >
                                {teams.map((team) => (
                                    <MenuItem key={team.id} value={team.id}>
                                        {team.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAddMapping}
                            disabled={!selectedActivityTemplateId || !selectedTeamId}
                        >
                            Add Mapping
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Process Template</TableCell>
                            <TableCell>Activity Template</TableCell>
                            <TableCell>Allowed Team</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {mappings.length > 0 ? (
                            mappings.map((mapping, index) => (
                                <TableRow key={index}>
                                    <TableCell>{mapping.process_template_name}</TableCell>
                                    <TableCell>{mapping.activity_template_name}</TableCell>
                                    <TableCell>{mapping.team_name}</TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            color="secondary"
                                            onClick={() => handleDeleteClick(mapping)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    No protected activities configured.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <ConfirmationBox
                isDialogOpen={deleteConfirmationOpen}
                title={`Delete Mapping: Are you sure you want to remove access restrictions for "${deleteTarget?.activity_template_name}" from team "${deleteTarget?.team_name}"?`}
                setIsDialogOpen={setDeleteConfirmationOpen}
                onYes={handleConfirmDelete}
            />
        </Box>
    )
}

export default ProtectedActivityConfig
